import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { NutritionProvider } from "@/contexts/NutritionContext";
import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Layout } from "./components/Layout";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { OfflineIndicator } from "./components/OfflineIndicator";
import { Dashboard } from "./pages/Dashboard";
import { Chat } from "./pages/Chat";
import { Workouts } from "./pages/Workouts";
import { GymWorkout } from "./pages/GymWorkout";
import { RunningWorkout } from "./pages/RunningWorkout";
import { RunningStart } from "./pages/RunningStart";
const RunningActive = lazy(() => import("./pages/RunningActive").then(m => ({ default: m.RunningActive })));
import { Progress } from "./pages/Progress";
import { FoodLog } from "./pages/FoodLog";
import { Recovery } from "./pages/Recovery";
import { Settings } from "./pages/Settings";
import NotFound from "./pages/NotFound";
import { Auth } from "./pages/Auth";
import { Onboarding } from "./pages/Onboarding";
import { TutorialPage } from "./pages/TutorialPage";
import { EducationalSlides } from "./pages/EducationalSlides";
import { TermsAndConditions } from "./pages/TermsAndConditions";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { Support } from "./pages/Support";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ResetPassword } from "./pages/ResetPassword";
import { AuthCallback } from "./pages/AuthCallback";
import UploadExerciseImages from "./pages/UploadExerciseImages";

const queryClient = new QueryClient();

const App = () => (
  <div className="bg-background" style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <NutritionProvider>
          <OfflineIndicator />
          <Toaster />
          <Sonner />
          <Routes>
          {/* Public routes (no Layout) */}
          <Route path="/" element={<Navigate to="/auth" replace />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/support" element={<Support />} />
          
          {/* Protected routes without Layout (onboarding flow) */}
          <Route 
            path="/onboarding" 
            element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tutorial" 
            element={
              <ProtectedRoute>
                <TutorialPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/educational-slides" 
            element={
              <ProtectedRoute>
                <EducationalSlides />
              </ProtectedRoute>
            } 
          />
          
          {/* Protected routes WITH shared Layout */}
          <Route 
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/chat" element={<ErrorBoundary><Chat /></ErrorBoundary>} />
            <Route path="/workouts" element={<Workouts />} />
            <Route path="/gym-workout" element={<GymWorkout />} />
            <Route path="/running-start" element={<RunningStart />} />
            <Route path="/running-workout" element={<RunningWorkout />} />
            <Route path="/running-active" element={<Suspense fallback={<div className="flex-1 flex items-center justify-center bg-background"><div className="animate-pulse text-muted-foreground">Loading...</div></div>}><RunningActive /></Suspense>} />
            <Route path="/progress" element={<Progress />} />
            <Route path="/food-log" element={<FoodLog />} />
            <Route path="/recovery" element={<Recovery />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/upload-exercise-images" element={<UploadExerciseImages />} />
          </Route>
          
          {/* Catch-all 404 route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </NutritionProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </div>
);

export default App;